import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { tap, shareReplay, take } from 'rxjs/operators';

interface CacheEntry {
  value: any;
  timestamp: number;
  observable?: Observable<any>;
}

@Injectable({
  providedIn: 'root'
})
export class ParametroCacheService {
  private cache = new Map<string, CacheEntry>();
  private pendingRequests = new Map<string, Observable<any>>();

  // TTL (Time To Live) in millisecondi - 2 minuti di default
  private readonly DEFAULT_TTL = 2 * 60 * 1000;

  // Subject per notificare quando la cache viene invalidata
  private cacheInvalidated$ = new Subject<string | null>();

  constructor() { }

  /**
   * Ottiene un valore dalla cache o esegue la chiamata HTTP se non presente/scaduto
   * @param key Chiave univoca (es: "parametro_1005" o "parametro_public_1010")
   * @param httpCall Funzione che ritorna l'Observable della chiamata HTTP
   * @param ttl Time to live in millisecondi (opzionale)
   */
  get<T>(key: string, httpCall: () => Observable<T>, ttl: number = this.DEFAULT_TTL): Observable<T> {
    // Controlla se esiste in cache e non è scaduto
    const cached = this.cache.get(key);
    if (cached && !this.isExpired(cached, ttl)) {
      return of(cached.value);
    }

    // Se c'è già una richiesta in corso per questa chiave, restituisci quella
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending;
    }

    // Esegui la chiamata HTTP e mettila in cache
    const request$ = httpCall().pipe(
      tap(value => {
        this.set(key, value);
        this.pendingRequests.delete(key);
      }),
      shareReplay(1),
      take(1)
    );

    this.pendingRequests.set(key, request$);
    return request$;
  }

  /**
   * Imposta un valore nella cache
   */
  private set(key: string, value: any): void {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  /**
   * Controlla se una entry è scaduta
   */
  private isExpired(entry: CacheEntry, ttl: number): boolean {
    return (Date.now() - entry.timestamp) > ttl;
  }

  /**
   * Invalida una specifica chiave dalla cache
   */
  invalidate(key: string): void {
    this.cache.delete(key);
    this.pendingRequests.delete(key);
    this.cacheInvalidated$.next(key);
  }

  /**
   * Invalida tutte le chiavi che iniziano con un prefisso
   */
  invalidateByPrefix(prefix: string): void {
    const keysToDelete: string[] = [];

    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.pendingRequests.delete(key);
    });

    this.cacheInvalidated$.next(prefix);
  }

  /**
   * Pulisce tutta la cache
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    this.cacheInvalidated$.next(null);
  }

  /**
   * Rimuove solo le entry scadute
   */
  cleanup(ttl: number = this.DEFAULT_TTL): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if ((now - entry.timestamp) > ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Observable per ascoltare le invalidazioni della cache
   */
  onCacheInvalidated(): Observable<string | null> {
    return this.cacheInvalidated$.asObservable();
  }

  /**
   * Ottiene informazioni di debug sulla cache
   */
  getCacheInfo(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}
